/**
 * CommunityNewsFeed — News tab content for a community.
 *
 * Layout:
 *   1. Hero story — large image + headline (featured or most recent)
 *   2. Photo cards row — 2-4 medium cards with thumbnails
 *   3. Headline list — compact text-only list for remaining articles
 *
 * Data flow:
 *   - Calls /api/News/feed/{scopeGuid} for approved articles
 *   - Falls back to /api/News/search for demo/prototype
 *   - canEdit users see a "Review" button that opens the moderation modal
 *
 * All user-facing strings use i18n via useAppTranslation('pages').
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchNewsFeed,
  fetchPendingItems,
  hideInheritedArticle,
  type NewsArticle,
  type NewsModerationItem,
} from '../../api/newsApi';
import { useApiCommunityData } from '../../api/useApiCommunityData';
import { updateCommunityEntity } from '../../api/communityMetadata';
import NewsCurateModal from './NewsCurateModal';
import ChroniclePickerModal from '../ChroniclePickerModal/ChroniclePickerModal';
import styles from './CommunityNewsFeed.module.css';
import { sanitizeEmbed } from '../../utils/sanitizeHtml';
import { mediaSrc } from '../../api/mediaUrl';
import FeedImage from '../FeedImage/FeedImage';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  communityGuid: string;
  communityName: string;
  canEdit: boolean;
  currentUserGuid?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function timeSince(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return formatDate(dateStr);
  } catch {
    return '';
  }
}

// openArticle is now a hook-based callback inside the component (see below)

/**
 * DEV-1466 (revised) — what belongs on the News tab.
 *
 * The first cut of this fix gated on content_type alone (allow api_article /
 * user_blog, block community_share). That was too blunt: it also blocked
 * platform content deliberately curated onto News via Sources ▸ L4E — a
 * shared chronicle / topic tracker is stored as `community_share`, so a
 * curated Topic Tracker ("Cold Fusion") silently vanished, and the Curate tab
 * fell out of sync with News.
 *
 * The real distinction is NOT the content_type — it's what the share IS:
 *
 *   news_featured   → auto-generated Feed companion record. Never on News.
 *
 *   community_share → two very different things (the Curate modal already
 *                     splits them this way — NewsCurateModal.tsx:493 vs :523):
 *       · WITH  chronicleGuid = PLATFORM content (chronicle, topic tracker).
 *         This is Life4Earth's own content and belongs on News — the
 *         Sources ▸ L4E tab exists to curate exactly these.
 *       · WITHOUT chronicleGuid = an EXTERNAL web link (the Facebook posts
 *         that started this ticket). These reach News ONLY when an admin has
 *         deliberately curated them (an explicit placement). An auto-approved,
 *         never-placed web share is feed noise — that is the actual leak, and
 *         it leaked because the News tab renders its `unassigned` bucket
 *         (no placement) into the hero/cards.
 *
 *   api_article / user_blog → curated types; always eligible.
 *
 * The one-way door still holds where it matters: a user's external link posted
 * to the feed never reaches News on its own. Gate is client-side because it
 * needs chronicleGuid + placement, which the `contentType` query param can't
 * express.
 */
function belongsOnNewsTab(m: NewsModerationItem): boolean {
  if (m.contentType === 'news_featured') return false;

  if (m.contentType === 'community_share') {
    if (m.article?.chronicleGuid) return true; // platform content (chronicle / topic tracker)
    return !!m.placement || !!m.isFeatured;    // external link: curated only
  }

  return true; // api_article, user_blog
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CommunityNewsFeed: React.FC<Props> = ({ communityGuid, communityName, canEdit, currentUserGuid }) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState<NewsModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState<string>('all');
  const [curateOpen, setCurateOpen] = useState(false);

  /** Chronicle picker — tracks which article is being shared to a chronicle. */
  const [chroniclePickerArticle, setChroniclePickerArticle] = useState<NewsArticle | null>(null);

  /** Open an article — chronicles use React Router (preserves history),
   *  external links open in a new tab. */
  const openArticle = useCallback((url: string) => {
    if (url.startsWith('/chronicles/')) {
      navigate(url, { state: { from: 'news', communityGuid, communityName } });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [navigate, communityGuid, communityName]);

  // ── Load feed data ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    // DEV-985 — Clear stale state immediately on community change. Without
    // this, navigating from a community with news (e.g. Rotary International)
    // to a community without news (e.g. Hampton Rotary) left the previous
    // community's articles visible until the user navigated away and back.
    // The bug was made worse by the old `feed.length > 0` guard below,
    // which silently skipped setFeedItems on empty results — so the stale
    // state never got overwritten.
    setFeedItems([]);
    setPendingCount(0);
    (async () => {
      setLoading(true);
      try {
        // Only show explicitly approved articles — never auto-fetch from provider.
        // DEV-1466: belongsOnNewsTab() keeps uncurated external feed shares and
        // news_featured companions off News, while letting curated platform
        // content (chronicles / topic trackers) through.
        const feedRaw = await fetchNewsFeed(communityGuid, { count: 50 });
        const feed = feedRaw.filter(belongsOnNewsTab);
        if (!cancelled) {
          // DEV-985: always set, even on empty — see comment above.
          setFeedItems(feed);
        }
      } catch {
        // Feed endpoint not ready — show empty state
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Load pending count for editors
      if (canEdit) {
        try {
          const pending = await fetchPendingItems(communityGuid);
          if (!cancelled) setPendingCount(pending.length);
        } catch {
          // Not critical
        }
      }
    })();
    return () => { cancelled = true; };
  }, [communityGuid, communityName, canEdit]);

  // DEV-987 — Build a lookup so the article-renderers below can pull
  // inherited-from attribution + moderationGuid for hide actions without
  // refactoring the whole article-array pipeline.
  const moderationByNewsItemGuid = useMemo(() => {
    const map = new Map<string, NewsModerationItem>();
    for (const item of feedItems) {
      if (item.newsItemGuid) map.set(item.newsItemGuid, item);
    }
    return map;
  }, [feedItems]);

  // DEV-987 — Master inheritance toggle for admins. Read the current
  // value from the community_entities row; flip via updateCommunityEntity.
  const apiCommunityData = useApiCommunityData();
  const currentInheritNews = useMemo<boolean>(() => {
    const cg = String(communityGuid || '').toUpperCase();
    const row = (apiCommunityData.communityEntities || []).find(
      (e: any) => String(e.entity_guid).toUpperCase() === cg,
    );
    const v = (row as any)?.inherit_news;
    // Default true (matches DB column default) when the row hasn't loaded
    // or the field isn't present (pre-migration rows during deploy window).
    return v === false ? false : true;
  }, [communityGuid, apiCommunityData.communityEntities]);

  const [inheritNewsDraft, setInheritNewsDraft] = useState<boolean | null>(null);
  // Reset the optimistic draft when the underlying value changes (community
  // switch, refetch after save, etc.).
  useEffect(() => { setInheritNewsDraft(null); }, [currentInheritNews, communityGuid]);
  const inheritNews = inheritNewsDraft ?? currentInheritNews;

  const handleToggleInherit = useCallback(async (next: boolean) => {
    setInheritNewsDraft(next);
    try {
      await updateCommunityEntity(communityGuid, { inheritNews: next });
      // Refetch the news feed so the cascade re-evaluates immediately.
      // DEV-1466: see belongsOnNewsTab().
      const feedRaw = await fetchNewsFeed(communityGuid, { count: 50 });
      setFeedItems(feedRaw.filter(belongsOnNewsTab));
    } catch (err: any) {
      console.warn('[News] Failed to toggle inherit_news:', err?.message || err);
      setInheritNewsDraft(!next); // optimistic rollback
    }
  }, [communityGuid]);

  // DEV-987 — Handler for the per-item "Hide" action on inherited articles.
  const handleHideInherited = useCallback(
    async (moderationGuid: string, sourceName: string | undefined) => {
      const confirmMsg = t('communityDetail.news.hideInheritedConfirm', {
        source: sourceName || t('communityDetail.news.hideInheritedFallbackSource'),
      });
      if (!window.confirm(confirmMsg)) return;
      try {
        await hideInheritedArticle(communityGuid, moderationGuid);
        setFeedItems((prev) => prev.filter((m) => m.moderationGuid !== moderationGuid));
      } catch (err: any) {
        console.warn('[News] Failed to hide inherited article:', err?.message || err);
      }
    },
    [communityGuid, t],
  );

  // ── Derive articles for category filter chips ─────────────
  const articles = feedItems.map((m) => m.article).filter(Boolean);

  // ── Filter and split by placement ─────────────────────────
  const filtered = filter === 'all'
    ? feedItems
    : feedItems.filter((m) => m.article?.category?.toLowerCase() === filter);

  // Use placement metadata from backend instead of positional slicing
  const featuredItems = filtered.filter((m) => m.placement === 'featured' || (!m.placement && m.isFeatured));
  const cardItems = filtered.filter((m) => m.placement === 'card');
  const headlineItems = filtered.filter((m) => m.placement === 'headline');
  const unassigned = filtered.filter((m) => !m.placement && !m.isFeatured);

  // Hero: first featured, or first unassigned
  let hero: NewsArticle | null = featuredItems[0]?.article ?? null;
  const extraFeatured = featuredItems.slice(1);

  if (!hero && unassigned.length > 0) {
    hero = unassigned[0]?.article ?? null;
  }

  // Cards: explicit cards + overflow featured + remaining unassigned
  const cards: NewsArticle[] = [
    ...extraFeatured.map((m) => m.article),
    ...cardItems.map((m) => m.article),
    ...(hero === unassigned[0]?.article ? unassigned.slice(1) : unassigned).map((m) => m.article),
  ].filter(Boolean) as NewsArticle[];

  // Headlines: explicit headlines
  const headlines: NewsArticle[] = headlineItems.map((m) => m.article).filter(Boolean) as NewsArticle[];

  // Get unique categories for filter chips
  const categories = Array.from(new Set(articles.map((a) => a.category).filter(Boolean))) as string[];

  // ── Feed layout ──────────────────────────────────────────
  return (
    <div className={styles.container}>
      {/* Header with filters and review button — always visible for editors */}
      <div className={styles.header}>
        {!loading && feedItems.length > 0 ? (
          <div className={styles.filterBar}>
            <button
              className={`${styles.filterBtn} ${filter === 'all' ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('all')}
            >
              {t('communityDetail.news.filterAll')}
            </button>
            {categories.slice(0, 4).map((cat) => (
              <button
                key={cat}
                className={`${styles.filterBtn} ${filter === cat ? styles.filterBtnActive : ''}`}
                onClick={() => setFilter(filter === cat ? 'all' : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        ) : (
          <div className={styles.filterBar} />
        )}

        <div className={styles.headerActions}>
          {/* DEV-987 — Master toggle: cascade approved news from ancestor
              communities (Rotary clubs see District / Zone / International
              articles with attribution). Visible only to admins. */}
          {canEdit && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                marginInlineEnd: 12,
              }}
              title={t('communityDetail.news.inheritToggleHint')}
            >
              <input
                type="checkbox"
                checked={inheritNews}
                onChange={(e) => handleToggleInherit(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>{t('communityDetail.news.inheritToggle')}</span>
            </label>
          )}
          {canEdit && (
            <button
              className={styles.reviewBtn}
              onClick={() => setCurateOpen(true)}
            >
              {t('communityDetail.news.curate')}
              {pendingCount > 0 && (
                <span className={styles.pendingBadge}>{pendingCount}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles.loading}>
          {t('communityDetail.news.loading')}
        </div>
      )}

      {/* Empty state */}
      {!loading && feedItems.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📰</div>
          <h3 className={styles.emptyTitle}>
            {t('communityDetail.news.emptyTitle')}
          </h3>
          <p className={styles.emptyHint}>
            {t('communityDetail.news.emptyHint')}
          </p>
        </div>
      )}

      {/* Hero story — text left, image right (flips in RTL) */}
      {hero && (
        <article className={styles.hero} onClick={() => openArticle(hero.canonicalUrl)}>
          <div className={styles.heroBody}>
            <span className={styles.featuredBadge}>
              {t('communityDetail.news.featured')}
            </span>
            <div className={styles.heroSource}>
              <span>{hero.sourceName || hero.sourceDomain}</span>
              {hero.category && <span>·&nbsp;{hero.category}</span>}
            </div>
            <h2 className={styles.heroTitle}>{hero.title}</h2>
            {hero.excerpt && (
              <p className={styles.heroExcerpt}>{hero.excerpt}</p>
            )}
            <div className={styles.heroDate}>{timeSince(hero.publishedAt)}</div>
          </div>
          {hero.imageUrl ? (
            <FeedImage
              className={styles.heroImage}
              src={mediaSrc(hero.imageUrl)}
              fallback={<div className={styles.heroImagePlaceholder}>📰</div>}
            />
          ) : (
            <div className={styles.heroImagePlaceholder}>📰</div>
          )}
        </article>
      )}

      {/* Medium cards row */}
      {cards.length > 0 && (
        <div className={styles.cardsRow}>
          {cards.map((article) => (
            <article
              key={article.newsItemGuid || article.canonicalUrl}
              className={styles.card}
              onClick={() => openArticle(article.canonicalUrl)}
            >
              {/* Video embed for shared videos */}
              {article.embedHtml && article.embedType === 'video' ? (
                <div
                  className={styles.cardEmbed}
                  onClick={(e) => e.stopPropagation()}
                  dangerouslySetInnerHTML={{ __html: sanitizeEmbed(article.embedHtml) }}
                />
              ) : article.imageUrl ? (
                <FeedImage
                  className={styles.cardImage}
                  src={mediaSrc(article.imageUrl)}
                  fallback={<div className={styles.cardImagePlaceholder}>📄</div>}
                />
              ) : (
                <div className={styles.cardImagePlaceholder}>📄</div>
              )}
              <div className={styles.cardBody}>
                <div className={styles.cardSource}>
                  {article.sourceName || article.sourceDomain}
                  {currentUserGuid && article.newsItemGuid && (
                    <button
                      type="button"
                      className={styles.shareToChronicleBtn}
                      title={t('communityDetail.feedShareToChronicle')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setChroniclePickerArticle(article);
                      }}
                    >
                      📋
                    </button>
                  )}
                </div>
                <h3 className={styles.cardTitle}>{article.title}</h3>
                {/* DEV-987 — inheritance attribution + admin hide. */}
                {(() => {
                  const mod = article.newsItemGuid ? moderationByNewsItemGuid.get(article.newsItemGuid) : undefined;
                  if (!mod?.inheritedFromGuid) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)' }}>
                      <span style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 4,
                        padding: '2px 6px',
                        fontStyle: 'italic',
                      }}>
                        {t('communityDetail.news.viaSource', { source: mod.inheritedFromName || t('communityDetail.news.hideInheritedFallbackSource') })}
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleHideInherited(mod.moderationGuid, mod.inheritedFromName || undefined);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-tertiary)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: 11,
                            padding: 0,
                          }}
                        >
                          {t('communityDetail.news.hideInherited')}
                        </button>
                      )}
                    </div>
                  );
                })()}
                {article.shareComment && (
                  <p className={styles.cardComment}>{article.shareComment}</p>
                )}
                <div className={styles.cardDate}>{timeSince(article.publishedAt)}</div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Headline list */}
      {headlines.length > 0 && (
        <div className={styles.headlineSection}>
          <div className={styles.headlineSectionTitle}>
            {t('communityDetail.news.moreStories')}
          </div>
          {headlines.map((article) => (
            <div
              key={article.newsItemGuid || article.canonicalUrl}
              className={styles.headlineItem}
              onClick={() => openArticle(article.canonicalUrl)}
            >
              <div className={styles.headlineDot} />
              <div className={styles.headlineContent}>
                <h4 className={styles.headlineTitle}>{article.title}</h4>
                <div className={styles.headlineMeta}>
                  <span className={styles.headlineSourceName}>
                    {article.sourceName || article.sourceDomain}
                  </span>
                  <span>{timeSince(article.publishedAt)}</span>
                  {/* DEV-987 — inheritance attribution + admin hide. */}
                  {(() => {
                    const mod = article.newsItemGuid ? moderationByNewsItemGuid.get(article.newsItemGuid) : undefined;
                    if (!mod?.inheritedFromGuid) return null;
                    return (
                      <>
                        <span style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          padding: '1px 6px',
                          fontStyle: 'italic',
                          fontSize: 10,
                        }}>
                          {t('communityDetail.news.viaSource', { source: mod.inheritedFromName || t('communityDetail.news.hideInheritedFallbackSource') })}
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHideInherited(mod.moderationGuid, mod.inheritedFromName || undefined);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--text-tertiary)',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontSize: 10,
                              padding: 0,
                            }}
                          >
                            {t('communityDetail.news.hideInherited')}
                          </button>
                        )}
                      </>
                    );
                  })()}
                  {currentUserGuid && article.newsItemGuid && (
                    <button
                      type="button"
                      className={styles.shareToChronicleBtn}
                      title={t('communityDetail.feedShareToChronicle')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setChroniclePickerArticle(article);
                      }}
                    >
                      📋
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Curate Modal — Configure + Curate tabs */}
      <NewsCurateModal
        communityGuid={communityGuid}
        communityName={communityName}
        isOpen={curateOpen}
        onClose={() => setCurateOpen(false)}
        onChanged={() => {
          // Refresh feed items (with placement metadata) and pending count.
          // DEV-1466: see belongsOnNewsTab().
          fetchNewsFeed(communityGuid, { count: 50 })
            .then((feed) => setFeedItems(feed.filter(belongsOnNewsTab)))
            .catch(() => {});
          fetchPendingItems(communityGuid)
            .then((p) => setPendingCount(p.length))
            .catch(() => {});
        }}
      />

      {/* Share to Chronicle Picker */}
      {currentUserGuid && (
        <ChroniclePickerModal
          isOpen={chroniclePickerArticle !== null}
          onClose={() => setChroniclePickerArticle(null)}
          newsItemGuid={chroniclePickerArticle?.newsItemGuid || ''}
          articleTitle={chroniclePickerArticle?.title || ''}
          currentUserGuid={currentUserGuid}
          onShared={() => setChroniclePickerArticle(null)}
        />
      )}

    </div>
  );
};

export default CommunityNewsFeed;
